import { AudioDeviceService } from "@/features/audio/interfaces/browser-services.interface";
import { DeviceRouter } from "@/features/audio/interfaces/device-router.interface";
import { AudioStateStore } from "@/features/audio/services/audio-state.store";
import { AudioDevice, ResolvedAudioDeviceSelection } from "@/features/audio/types/audio-device.types";

export class DeviceManager {
    private unsubscribeDeviceChanges: (() => void) | null = null;

    constructor(
        private readonly audioDeviceService: AudioDeviceService,
        private readonly stateStore: AudioStateStore,
        private deviceRouter: DeviceRouter | null = null
    ) {}

    setDeviceRouter(deviceRouter: DeviceRouter): void {
        this.deviceRouter = deviceRouter;
    }

    async resolveStoredDevices(options?: { persistResolved?: boolean; unnamedLabel?: string }) {
        const result = await this.audioDeviceService.resolveStoredDevices(options);
        this.stateStore.patch({
            devices: {
                inputDeviceId: result.inputDeviceId,
                outputDeviceId: result.outputDeviceId,
                devices: result.devices,
                isComplete: result.isComplete,
            },
        });
        return result;
    }

    persistSelection(selection: ResolvedAudioDeviceSelection): void {
        this.audioDeviceService.persistSelection(selection);
        this.stateStore.patch({
            devices: {
                ...this.stateStore.getState().devices,
                inputDeviceId: selection.inputDeviceId,
                outputDeviceId: selection.outputDeviceId,
                isComplete: selection.isComplete,
            },
        });
    }

    async applyResolvedDevices(resolved: ResolvedAudioDeviceSelection): Promise<void> {
        const { canSelectMicrophone, canSelectSpeaker } = this.stateStore.getState().capabilities;

        if (resolved.inputDeviceId && canSelectMicrophone) {
            await this.deviceRouter?.setInputDevice(resolved.inputDeviceId);
        }
        if (resolved.outputDeviceId && canSelectSpeaker) {
            await this.deviceRouter?.setOutputDevice(resolved.outputDeviceId);
        }

        this.stateStore.patch({
            devices: {
                ...this.stateStore.getState().devices,
                inputDeviceId: resolved.inputDeviceId,
                outputDeviceId: resolved.outputDeviceId,
                isComplete: resolved.isComplete,
            },
        });
    }

    async setInputDevice(deviceId: string): Promise<void> {
        if (!this.stateStore.getState().capabilities.canSelectMicrophone) {
            return;
        }

        await this.deviceRouter?.setInputDevice(deviceId);
        this.stateStore.patch({
            devices: {
                ...this.stateStore.getState().devices,
                inputDeviceId: deviceId,
            },
        });
    }

    async setOutputDevice(deviceId: string): Promise<void> {
        if (!this.stateStore.getState().capabilities.canSelectSpeaker) {
            return;
        }

        await this.deviceRouter?.setOutputDevice(deviceId);
        this.stateStore.patch({
            devices: {
                ...this.stateStore.getState().devices,
                outputDeviceId: deviceId,
            },
        });
    }

    detectListDiff(previous: AudioDevice[] | null, current: AudioDevice[]) {
        return this.audioDeviceService.detectListDiff(previous, current);
    }

    startDeviceChangeSync(onChange: () => void): () => void {
        this.unsubscribeDeviceChanges?.();
        this.unsubscribeDeviceChanges = this.audioDeviceService.subscribeToDeviceChanges(onChange);
        return () => {
            this.unsubscribeDeviceChanges?.();
            this.unsubscribeDeviceChanges = null;
        };
    }
}
